/**
 * ImportSheetModal — runs the historical Google Sheet import and shows the
 * run summary (created/updated/skipped + any low-confidence column mappings to
 * spot-check). Idempotent on the backend, so re-running is safe.
 */

import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { importSheet } from '../../services/invoicingApi';
import { ImportSummary, SheetInspection } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

/** Fields surfaced in the dry-run preview (identity + the money columns). */
const PREVIEW_FIELDS = [
  'advertiser',
  'campaignOrderId',
  'billableTotal',
  'plannedTotal',
  'contractedImpressions',
  'impressionsDelivered1P',
  'impressionsDelivered3P',
];

const ImportSheetModal: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inspectTab, setInspectTab] = useState('');
  const [purge, setPurge] = useState(false);

  const run = async (dryRun: boolean) => {
    setRunning(true);
    setError(null);
    setSummary(null);
    try {
      const tabs = inspectTab.trim() ? [inspectTab.trim()] : undefined;
      const res = await importSheet(dryRun ? { dryRun: true, tabs } : { tabs, purge });
      setSummary(res.summary);
      if (!dryRun) onImported();
    } catch (err: any) {
      setError(err?.message || (dryRun ? 'Inspection failed.' : 'Import failed.'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Invoicing"
      title="Import from Google Sheet"
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={running}>
            Close
          </Button>
          <Button variant="ghost" onClick={() => run(true)} disabled={running}>
            {running ? 'Working…' : 'Inspect (dry run)'}
          </Button>
          <Button variant="primary" onClick={() => run(false)} disabled={running}>
            {running ? 'Importing…' : summary && !summary.dryRun ? 'Run again' : 'Run import'}
          </Button>
        </>
      }
    >
      <p style={styles.intro}>
        Auto-finds both "Monthly Invoicing Tracker" workbooks (2025 + 2026) in Drive and scrapes
        every monthly tab (e.g. "MARCH 2026"), upserting each row into the tracker. Existing rows
        are refreshed without overwriting any manual edits.
      </p>
      <p style={styles.note}>
        Both spreadsheets must be shared (at least Viewer) with{' '}
        <strong>culture-genius@culturegenesis.com</strong>. If you see an "access denied" error
        below, share them with that account and run the import again.
      </p>
      <div style={styles.inspectRow}>
        <input
          style={styles.input}
          placeholder="Tab to limit (optional, e.g. MAY 2026)"
          value={inspectTab}
          onChange={(e) => setInspectTab(e.target.value)}
          disabled={running}
        />
        <span style={styles.note}>
          Use <strong>Inspect (dry run)</strong> to preview the detected columns &amp; values
          without writing anything. A tab here scopes both inspect and import.
        </span>
        <label style={styles.checkRow}>
          <input
            type="checkbox"
            checked={purge}
            onChange={(e) => setPurge(e.target.checked)}
            disabled={running}
          />
          <span>
            Replace previous import (delete earlier imported rows for these months first — clears
            duplicates; your manual edits are preserved)
          </span>
        </label>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {summary && (
        <div style={styles.summary}>
          <div style={styles.statRow}>
            <Stat label={summary.dryRun ? 'Tabs inspected' : 'Tabs'} value={summary.tabsProcessed.length} />
            <Stat label="Rows read" value={summary.rowsRead} />
            {!summary.dryRun && <Stat label="Created" value={summary.created} />}
            {!summary.dryRun && <Stat label="Updated" value={summary.updated} />}
            {!summary.dryRun && <Stat label="Skipped" value={summary.skipped} />}
            {!summary.dryRun && summary.purged != null && summary.purged > 0 && (
              <Stat label="Replaced" value={summary.purged} />
            )}
          </div>
          {summary.dryRun && (
            <div style={styles.note}>
              <strong>Dry run</strong> — nothing was written. Confirm each money column resolves to
              the right header below, then run the real import.
            </div>
          )}
          {summary.inspections && summary.inspections.length > 0 && (
            <div style={styles.inspections}>
              {summary.inspections.map((insp, i) => (
                <Inspection key={i} insp={insp} />
              ))}
            </div>
          )}
          {!summary.dryRun && summary.skipped > 0 && summary.skipReasons && (
            <div style={styles.note}>
              Skipped breakdown: {summary.skipReasons.noAdvertiser} no-advertiser rows (blank /
              total / continuation lines), {summary.skipReasons.noMonth} no-month,{' '}
              {summary.skipReasons.upsertFailed} write errors.
            </div>
          )}
          {summary.tabsProcessed.length > 0 && (
            <div style={styles.note}>Tabs: {summary.tabsProcessed.join(', ')}</div>
          )}
          {summary.lowConfidenceCols.length > 0 && (
            <div style={styles.warnBox}>
              <strong>Spot-check these column mappings:</strong>
              <ul style={styles.list}>
                {summary.lowConfidenceCols.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}
          {summary.parseErrors.length > 0 && (
            <div style={styles.errBox}>
              <strong>{summary.parseErrors.length} parse error(s):</strong>
              <ul style={styles.list}>
                {summary.parseErrors.slice(0, 10).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div style={styles.stat}>
    <div style={styles.statValue}>{value}</div>
    <div style={styles.statLabel}>{label}</div>
  </div>
);

/** 0-based column index → spreadsheet letter (0 → A). */
const colLetterFE = (index: number): string => {
  let s = '';
  let n = index + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/** Dry-run preview of one tab: how each field mapped + every column's values. */
const Inspection: React.FC<{ insp: SheetInspection }> = ({ insp }) => {
  const colOf = (field: string) => insp.columns.find((c) => c.field === field);
  const preview = PREVIEW_FIELDS.map((f) => colOf(f)).filter(
    (c): c is NonNullable<typeof c> => !!c && c.columnIndex >= 0,
  );
  const samples = insp.sampleRows.slice(0, 5);
  const headers = insp.allHeaders ?? [];

  return (
    <div style={styles.inspBox}>
      <div style={styles.inspTitle}>
        {insp.workbook} › {insp.tab}
        {insp.monthKey ? ` · ${insp.monthKey}` : ''} · header row {insp.headerRowIndex + 1}
      </div>

      <div style={styles.inspSubhead}>Resolved field mapping</div>
      <table style={styles.inspTable}>
        <thead>
          <tr>
            <th style={styles.inspTh}>Field</th>
            <th style={styles.inspTh}>Col</th>
            <th style={styles.inspTh}>Header</th>
            {samples.map((_, i) => (
              <th key={i} style={styles.inspTh}>
                Row {i + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.map((c) => (
            <tr key={c.field}>
              <td style={styles.inspTdField}>{c.field}</td>
              <td style={styles.inspTd}>{c.columnLetter}</td>
              <td style={styles.inspTd}>{c.header}</td>
              {samples.map((row, i) => (
                <td key={i} style={styles.inspTd}>
                  {String(row[c.columnIndex] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {insp.unmapped.length > 0 && (
        <div style={styles.inspUnmapped}>Unmapped fields: {insp.unmapped.join(', ')}</div>
      )}

      {headers.length > 0 && (
        <>
          <div style={styles.inspSubhead}>All columns (raw)</div>
          <table style={styles.inspTable}>
            <thead>
              <tr>
                <th style={styles.inspTh}>Col</th>
                <th style={styles.inspTh}>Header</th>
                {samples.map((_, i) => (
                  <th key={i} style={styles.inspTh}>
                    Row {i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {headers.map((h, ci) => (
                <tr key={ci}>
                  <td style={styles.inspTd}>{colLetterFE(ci)}</td>
                  <td style={styles.inspTdField}>{h || '(blank)'}</td>
                  {samples.map((row, i) => (
                    <td key={i} style={styles.inspTd}>
                      {String(row[ci] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  intro: { fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.5, margin: '0 0 12px' },
  error: {
    padding: '10px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    marginBottom: 12,
  },
  summary: { display: 'flex', flexDirection: 'column', gap: 12 },
  statRow: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  stat: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 },
  statValue: { fontSize: 22, fontWeight: 700, color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' },
  statLabel: { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--fg-4)' },
  note: { fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.5, margin: '0 0 12px' },
  warnBox: {
    padding: '10px 12px',
    background: 'var(--warning-soft)',
    color: 'var(--warning)',
    borderRadius: 'var(--radius-md)',
    fontSize: 12.5,
  },
  errBox: {
    padding: '10px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 12.5,
  },
  list: { margin: '6px 0 0', paddingLeft: 18 },
  inspectRow: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
  checkRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 12,
    color: 'var(--fg-2)',
    lineHeight: 1.4,
    cursor: 'pointer',
  },
  input: {
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    width: '100%',
    boxSizing: 'border-box',
  },
  inspections: { display: 'flex', flexDirection: 'column', gap: 12 },
  inspBox: {
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    padding: 10,
    overflowX: 'auto',
  },
  inspTitle: { fontSize: 12.5, fontWeight: 700, color: 'var(--fg-1)', marginBottom: 8 },
  inspSubhead: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
    margin: '10px 0 4px',
  },
  inspTable: { borderCollapse: 'collapse', fontSize: 11.5, width: '100%' },
  inspTh: {
    textAlign: 'left',
    fontWeight: 700,
    color: 'var(--fg-4)',
    padding: '3px 8px',
    borderBottom: '1px solid var(--border-1)',
    whiteSpace: 'nowrap',
  },
  inspTd: { padding: '3px 8px', color: 'var(--fg-2)', whiteSpace: 'nowrap', verticalAlign: 'top' },
  inspTdField: {
    padding: '3px 8px',
    color: 'var(--fg-1)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  },
  inspUnmapped: { fontSize: 11.5, color: 'var(--warning)', marginTop: 8 },
};

export default ImportSheetModal;
